import { useEffect, useState } from "react"
import { useSpring, animated } from "react-spring"

import LogoHorizontal from "@src/components/LogoHorizontal"

import classNames from "@src/utilities/class-names"

function Spinner({ size = 100 }) {
  const multiplier = size / 100

  return (
    <svg
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${size} ${size}`}
      preserveAspectRatio="xMidYMid"
      data-component="loading-spinner"
    >
      <circle
        cx={0.5 * size}
        cy={0.5 * size}
        fill="none"
        className="stroke-black"
        strokeWidth={0.08 * size}
        r={0.44 * size}
        strokeDasharray={`${207.34511513692632 * multiplier} ${
          71.11503837897544 * multiplier
        }`}
        transform={`rotate(${143.937 * multiplier} ${0.5 * size} ${
          0.5 * size
        })`}
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          calcMode="linear"
          values={`0 ${0.5 * size} ${0.5 * size}; 360 ${0.5 * size} ${
            0.5 * size
          }`}
          keyTimes="0;1"
          dur="1s"
          begin="0s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  )
}

interface LoadingFullScreenProps {
  showing: boolean
  text?: string
}
export default function LoadingFullScreen({
  showing,
  text: textToDisplay,
}: LoadingFullScreenProps) {
  const [animationOpacity, setAnimationOpacity] = useState<number>(0)
  const [showSpinner, setShowSpinner] = useState<boolean>(false)

  const isShowingScreen = useSpring({
    from: { opacity: 1 },
    to: { opacity: showing ? 1 : 0 },
    config: { duration: 300 },
  })

  const onyx = useSpring({
    from: { opacity: 0 },
    to: { opacity: animationOpacity },
    config: { delay: 0, duration: 300 },
  })

  const plus = useSpring({
    from: { opacity: 0 },
    to: { opacity: animationOpacity },
    config: { delay: 150, duration: 600 },
  })

  const rose = useSpring({
    from: { opacity: 0 },
    to: { opacity: animationOpacity },
    config: { delay: 300, duration: 900 },
  })

  const underline = useSpring({
    from: { opacity: 0 },
    to: { opacity: animationOpacity },
    config: { delay: 0, duration: 1200 },
  })

  const spinner = useSpring({
    from: { opacity: 0 },
    to: { opacity: showSpinner ? 1 : 0 },
    config: { delay: 0, duration: 300 },
  })

  const text = useSpring({
    from: { opacity: 0 },
    to: { opacity: animationOpacity },
    config: { delay: 0, duration: 600 },
  })

  useEffect(() => {
    setAnimationOpacity(1)
    setTimeout(() => {
      setShowSpinner(true)
    }, 1000)
  }, [])

  return (
    <animated.div
      className={classNames(
        "bg-white fixed h-full left-0 top-0 w-full z-[999999]",
        showing ? "" : "pointer-events-none"
      )}
      style={isShowingScreen}
    >
      <div className="flex h-full items-center justify-center w-full">
        <div className="w-11/12 max-w-xs">
          <div className="text-center">
            <LogoHorizontal
              className="fill-black"
              onyxSpringStyle={onyx}
              plusSpringStyle={plus}
              roseSpringStyle={rose}
              underlineSpringStyle={underline}
            />
            {textToDisplay && (
              <animated.p
                className="font-browser text-sm mt-2 tracking-wide uppercase"
                dangerouslySetInnerHTML={{ __html: textToDisplay }}
                style={text}
              />
            )}
            <animated.div
              className={classNames(
                "mt-4 inline-block",
                showSpinner ? "visible" : "invisible"
              )}
              style={spinner}
            >
              <Spinner size={30} />
            </animated.div>
          </div>
        </div>
      </div>
    </animated.div>
  )
}
